import Business from '@/types/business';
import User from '@/types/user';
import { makeBFFAPICall } from './axiosInstance';

export const INTERCOM_APP_ID = 'jktn9lf9';

export const isChatEnabled = () => {
    const environment = process.env.NEXT_PUBLIC_ENVIRONMENT;

    return environment !== 'staging' && environment !== 'qa';
};

export interface ChatUser {
    userId: number | undefined;
    business: Business | undefined;
    isInternalUser: boolean | undefined;
}

const withIntercom = ( fn: ( intercom: NonNullable<Window['Intercom']> ) => void, attempt = 0 ) => {
    if ( typeof window === 'undefined' ) {
        return;
    }

    if ( window.Intercom ) {
        fn( window.Intercom );
        return;
    }

    if ( attempt >= 20 ) {
        return;
    }

    setTimeout( () => withIntercom( fn, attempt + 1 ), 250 );
};

let bootedKey: string | undefined;

const getBusinessUserId = ( business: Business | undefined ) => {
    return business?.businessUserId || business?.businessuserId;
};

export const updateChatAlignment = ( alignment: 'left' | 'right' ) => {
    withIntercom( intercom => {
        intercom( 'update', { alignment } );
    } );
};

export const bootChat = ( {
    userId
    , business
    , isInternalUser
}: ChatUser ) => {

    if ( !isChatEnabled() || isInternalUser ) {
        return;
    }

    const businessId = business?.id;
    const businessUserId = getBusinessUserId( business );

    const key = userId && businessId && businessUserId
        ? `${ userId }:${ businessId }:${ businessUserId }`
        : undefined;

    if ( !key ) {
        console.error( 'Missing ids for Intercom messenger token' );
        return;
    }

    if ( bootedKey === key ) {
        return;
    }

    if ( bootedKey && typeof window !== 'undefined' && window.Intercom ) {
        window.Intercom( 'shutdown' );
    }

    bootedKey = key;

    const bootWithJwt = async () => {
        try {
            const result = await makeBFFAPICall<{ token: string }>( {
                method: 'GET'
                , url: `/businesses/${ businessId }/business-users/${ businessUserId }/intercom-messenger-tokens`
            } );

            if ( !result || !result.token ) {
                console.error( 'No token in response from intercom messenger token endpoint' );

                if ( bootedKey === key ) {
                    bootedKey = undefined;
                }

                return;
            }

            withIntercom( intercom => {
                intercom( 'boot', {
                    app_id: INTERCOM_APP_ID
                    , hide_default_launcher: true
                    , alignment: 'left'
                    , intercom_user_jwt: result.token
                } );
            } );
        } catch ( e ) {
            if ( bootedKey === key ) {
                bootedKey = undefined;
            }

            if ( e && e instanceof Error ) {
                console.error( `Error getting Intercom messenger token - ${ e.message }` );
            }
        }
    };

    void bootWithJwt();
};

export const openChat = () => {
    withIntercom( intercom => intercom( 'show' ) );
};

export const logoutChat = () => {
    bootedKey = undefined;

    if ( typeof window !== 'undefined' && window.Intercom ) {
        window.Intercom( 'shutdown' );
    }
};

export const authenticateChat = ( user: User, business: Business ) => {
    try {
        if ( !user || !user.id ) {
            console.error( 'No user id present' );
        }

        logoutChat();
        bootChat( {
            userId: user.id
            , business
            , isInternalUser: user.isInternalUser
        } );
    } catch ( e ) {
        if ( e && e instanceof Error ) {
            console.error( `Error authenticating chat - ${ e.message }` );
        }
    }
};
